"use client";

import { c as _c } from "react/compiler-runtime";
import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { BAR_HEIGHTS } from "./bar-heights";
import s from "./visuals.module.css";
import type { CSSProperties } from "react";
export function AudioPlayback(t0) {
  const $ = _c(65);
  const {
    mode,
    src,
    words,
    times
  } = t0;
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(times.at(-1)?.[1] ?? 1);
  let t1;
  let t2;
  if ($[0] !== playing) {
    t1 = () => {
      if (!playing) {
        return;
      }
      let frame = 0;
      const tick = () => {
        setTime(audioRef.current?.currentTime ?? 0);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    };
    t2 = [playing];
    $[0] = playing;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const toggle = t3;
  const started = playing || time > 0;
  const progress = time / duration;
  let t4;
  if ($[4] !== started || $[5] !== time || $[6] !== times) {
    let t5;
    if ($[8] !== started || $[9] !== time) {
      t5 = t6 => {
        const [start, end] = t6;
        return started && time >= start && time < end;
      };
      $[8] = started;
      $[9] = time;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = times.findIndex(t5);
    $[4] = started;
    $[5] = time;
    $[6] = times;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const active = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setPlaying(true);
    t6 = () => setPlaying(false);
    t7 = () => {
      setPlaying(false);
      setTime(0);
    };
    t8 = event => setDuration(event.currentTarget.duration);
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== src) {
    t9 = <audio ref={audioRef} src={src} preload="metadata" onPlay={t5} onPause={t6} onEnded={t7} onLoadedMetadata={t8} />;
    $[15] = src;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = playing ? "Pause audio" : "Play audio";
  let t11;
  if ($[17] !== playing) {
    t11 = playing ? <Pause className="size-4 fill-current" /> : <Play className="size-4 translate-x-px fill-current" />;
    $[17] = playing;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <button type="button" onClick={toggle} aria-label={t10} className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--fa)] text-white transition-transform duration-150 ease-out active:scale-[0.94] dark:text-zinc-950">{t11}</button>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== playing || $[23] !== progress || $[24] !== started) {
    t13 = BAR_HEIGHTS.map((height, i) => {
      const position = i / BAR_HEIGHTS.length;
      const reached = !started || position < progress;
      return <span key={i} className={cn("min-w-[2px] flex-1 rounded-full transition-colors duration-150", playing && s.wave, reached ? "bg-[var(--fa)]" : "bg-muted-foreground/30")} style={{
        "--i": i,
        height: `${height * 100}%`
      } as CSSProperties} />;
    });
    $[22] = playing;
    $[23] = progress;
    $[24] = started;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <div className="flex h-14 min-w-0 flex-1 items-center gap-[3px]" aria-hidden={true}>{t13}</div>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = started ? time : duration;
  let t16;
  if ($[28] !== t15) {
    t16 = t15.toFixed(1);
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16) {
    t17 = <span className="w-10 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground">{t16}s</span>;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t12 || $[33] !== t14 || $[34] !== t17 || $[35] !== t9) {
    t18 = <div className="flex items-center gap-3 rounded-xl border border-border bg-background/85 p-3">{t9}{t12}{t14}{t17}</div>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t17;
    $[35] = t9;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const player = t18;
  if (mode === "speech") {
    let t19;
    if ($[37] !== active || $[38] !== started || $[39] !== time || $[40] !== times || $[41] !== words) {
      let t20;
      if ($[43] !== active || $[44] !== started || $[45] !== time || $[46] !== times) {
        t20 = (word, i_0) => {
          const spoken = started && time >= times[i_0][1];
          return <span key={word} className={cn("rounded px-0.5 transition-colors duration-150", i_0 === active && "bg-[color-mix(in_oklab,var(--fa)_22%,transparent)]", !started || spoken || i_0 === active ? "text-foreground" : "text-muted-foreground")}>{word}</span>;
        };
        $[43] = active;
        $[44] = started;
        $[45] = time;
        $[46] = times;
        $[47] = t20;
      } else {
        t20 = $[47];
      }
      t19 = words.map(t20);
      $[37] = active;
      $[38] = started;
      $[39] = time;
      $[40] = times;
      $[41] = words;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    let t20;
    if ($[48] !== t19) {
      t20 = <p className="flex flex-wrap gap-x-1 gap-y-1 text-lg font-medium leading-relaxed">{t19}</p>;
      $[48] = t19;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== player || $[51] !== t20) {
      t21 = <div className="space-y-5">{t20}{player}</div>;
      $[50] = player;
      $[51] = t20;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    return t21;
  }
  let t19;
  if ($[53] !== active || $[54] !== times || $[55] !== words) {
    let t20;
    if ($[57] !== active || $[58] !== times) {
      t20 = (word_0, i_1) => <span key={word_0} className={cn(s.pop, "rounded-lg border bg-background/85 px-2.5 py-1.5 transition-colors duration-150", i_1 === active ? "border-[var(--fa)] bg-[color-mix(in_oklab,var(--fa)_14%,transparent)]" : "border-border")} style={{
        "--i": i_1 + i_1
      } as CSSProperties}><span className="block font-mono text-[10px] text-muted-foreground">{times[i_1][0].toFixed(2)}s</span><span className="text-sm font-medium">{word_0}</span></span>;
      $[57] = active;
      $[58] = times;
      $[59] = t20;
    } else {
      t20 = $[59];
    }
    t19 = words.map(t20);
    $[53] = active;
    $[54] = times;
    $[55] = words;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[60] !== t19) {
    t20 = <div className="flex flex-wrap gap-2">{t19}</div>;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== player || $[63] !== t20) {
    t21 = <div className="space-y-5">{player}{t20}</div>;
    $[62] = player;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  return t21;
}